import * as React from "react"
import { useLang } from "../i18n"

const STORAGE_KEY = "tf_consent"
export const OPEN_CONSENT_EVENT = "tf:open-consent"

type Consent = "granted" | "denied"

const copy = {
  en: {
    message:
      "We use cookies to understand how visitors use our site and to improve it. Analytics cookies are only set if you accept.",
    accept: "Accept",
    decline: "Decline",
    aria: "Cookie consent",
  },
  ro: {
    message:
      "Folosim cookie-uri pentru a înțelege cum este folosit site-ul și pentru a-l îmbunătăți. Cookie-urile de analiză sunt setate doar dacă accepți.",
    accept: "Accept",
    decline: "Refuz",
    aria: "Consimțământ cookie-uri",
  },
}

const CookieConsent: React.FC = () => {
  const lang = useLang()
  const t = copy[lang] ?? copy.en
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    // No analytics configured → nothing to consent to, so never show the banner.
    if (!process.env.GATSBY_GA_MEASUREMENT_ID) return

    let stored: string | null = null
    try {
      stored = localStorage.getItem(STORAGE_KEY)
    } catch (e) {
      stored = null
    }
    if (stored !== "granted" && stored !== "denied") setVisible(true)

    const open = () => setVisible(true)
    window.addEventListener(OPEN_CONSENT_EVENT, open)
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open)
  }, [])

  const decide = (consent: Consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, consent)
    } catch (e) {
      // Storage may be unavailable (private mode); consent stays default-denied.
    }
    const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag
    if (consent === "granted" && typeof gtag === "function") {
      gtag("consent", "update", { analytics_storage: "granted" })
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label={t.aria}
      className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6"
    >
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-neutral-200 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-sm text-neutral-600 flex-1">{t.message}</p>
        <div className="flex gap-3 shrink-0">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-primary-500 border border-neutral-300 hover:bg-neutral-50 transition-colors"
          >
            {t.decline}
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-accent-600 hover:bg-accent-700 transition-colors"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieConsent
